import { makeStyles } from "tss-react/mui"
import { Box, DialogContent, Divider, Typography } from "@mui/material"
import { LocalizedStrict } from "../../../../../../modules/localization/components/AppLocalized"
import { toDinero } from "../../../../../../modules/currency/currencyLib"
import {
    fetchProductDetailsForUser,
    FetchProductDetailsForUserResponse,
    UserDetails,
} from "../../../../../../modules/nonAdminUser/nonAdminUserApi"

import { useMoney } from "../../../../../../modules/currency/useMoney"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { selectUserType } from "../../../../../../modules/login/loginSelectors"
import { UserType } from "../../../../../../modules/login/loginTypes"
import { useAppSelector } from "../../../../../../modules/store/storeHooks"
import { StandardDialog } from "../../../../dialogs/StandardDialog"
import { LoadingSpinner } from "../../../../loading/LoadingSpinner"

import { FavoriteButton } from "./FavoriteButton"
import ProductImg from "./ProductImg"

const useStyles = makeStyles()((theme) => ({
    dialogPaper: {
        maxWidth: "50rem",
    },
    dialogContent: {
        padding: "1rem 4rem 4rem",
        display: "grid",
        gap: "1rem",
        color: theme.palette.tailwind.slate[900],
    },
    imgWrap: {
        position: "relative",
    },
    box: {
        display: "grid",
        gap: "1rem",
        gridTemplateColumns: "5fr 1fr",
    },
    price: {
        textAlign: "right",
        flex: "none",
    },
}))

const transformData = (data: FetchProductDetailsForUserResponse) => data.product

type ProductDetailsDialogProps = Readonly<{
    userId: number
    productId: number
    isFavorite?: boolean
    // Omitted by surfaces that have no favorite mutation; the button is hidden instead of no-oping.
    onFavoriteToggle?: (productId: number, makeFavorite: boolean) => Promise<boolean>
    handleClose: () => void
}>

export function ProductDetailsDialog({
    userId,
    productId,
    isFavorite = false,
    onFavoriteToggle,
    handleClose,
}: ProductDetailsDialogProps) {
    const { classes } = useStyles()
    const moneyFactory = useMoney({})
    const userType = useAppSelector(selectUserType)
    const isOnBehalfOf = userType !== UserType.USER

    const { data: product, isLoading } = useApiQuery(fetchProductDetailsForUser, {
        queryName: UserDetails.USER_PRODUCT_DETAILS,
        dependencies: {
            userId,
            productId,
        },
        enabled: Boolean(userId) && Boolean(productId),
        select: transformData,
    })

    return (
        <StandardDialog
            open={!!product}
            onClose={handleClose}
            dialogTitle={product?.name || ""}
            slotProps={{
                paper: { className: classes.dialogPaper },
            }}
            maxWidthProp="md"
        >
            <DialogContent className={classes.dialogContent}>
                {!isLoading && product ? (
                    <>
                        <Box className={classes.imgWrap}>
                            <ProductImg imageUrl={product?.productPhoto?.url} size="large" />
                            {!isOnBehalfOf && onFavoriteToggle && (
                                <FavoriteButton
                                    isFavorite={isFavorite}
                                    placement="top-right"
                                    onClick={() => onFavoriteToggle(productId, !isFavorite)}
                                />
                            )}
                        </Box>
                        <Box className={classes.box}>
                            <Typography variant="body1">{product.description}</Typography>
                            <Typography variant="subtitle1" className={classes.price}>
                                {moneyFactory.format(toDinero(product.price))}
                            </Typography>
                        </Box>
                        {!!product.productDescription?.allergens?.length && (
                            <>
                                <Divider flexItem />
                                <Box className={classes.box}>
                                    <Box>
                                        <LocalizedStrict id="user-home-week-menu-dialog-allergens">
                                            <Typography variant="subtitle1">Allergens:</Typography>
                                        </LocalizedStrict>
                                        <Typography variant="body1">
                                            {product.productDescription.allergens.map((el) => el.name).join(" - ")}
                                        </Typography>
                                    </Box>
                                </Box>
                            </>
                        )}
                    </>
                ) : (
                    <LoadingSpinner />
                )}
            </DialogContent>
        </StandardDialog>
    )
}
